// Utility functions for generating and formatting months

export interface MonthInfo {
  monthStart: string; // YYYY-MM-01 format
  monthEnd: string;   // Last day of month
  label: string;      // Display label
}

/**
 * Generate an array of months from start date
 * @param startDate - Start date in YYYY-MM-DD format
 * @param numberOfMonths - Number of months to generate
 * @returns Array of month information
 */
export const generateMonths = (startDate: string, numberOfMonths: number = 12): MonthInfo[] => {
  const months: MonthInfo[] = [];
  const start = new Date(startDate);
  
  // Start from the beginning of the month
  const currentDate = new Date(start.getFullYear(), start.getMonth(), 1);
  console.log('currentDate', currentDate);
  for (let i = 0; i < numberOfMonths; i++) {
    const monthStart = new Date(currentDate);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0); // Last day of month
    
    const monthStartStr = monthStart.toISOString().split('T')[0];
    const monthEndStr = monthEnd.toISOString().split('T')[0];
    
    const monthLabel = monthStart.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short' 
    });
    
    // Correct calculation of monthStart and monthEnd for the given month
    months.push({
      monthStart: `${monthStart.getFullYear()}-${String(monthStart.getMonth() + 1).padStart(2, '0')}-01`,
      monthEnd: monthEndStr, // Already correct
      label: monthLabel,
    });
    
    // Move to next month
    currentDate.setMonth(currentDate.getMonth() + 1);
  }
  
  return months;
};

/**
 * Format date for display
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Formatted date string
 */
export const formatDate = (dateStr: string): string => {
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
};

/**
 * Get month key from date (YYYY-MM format)
 * @param dateStr - Date string in YYYY-MM-DD format
 * @returns Month key in YYYY-MM format
 */
export const getMonthKey = (dateStr: string): string => {
  const date = new Date(dateStr);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}`;
};

/**
 * Calculate number of months between two dates
 * @param startDate - Start date in YYYY-MM-DD format
 * @param endDate - End date in YYYY-MM-DD format
 * @returns Number of months (can be fractional)
 */
export const calculateMonthsBetween = (startDate: string, endDate: string): number => {
  const start = new Date(startDate);
  const end = new Date(endDate);
  
  const yearDiff = end.getFullYear() - start.getFullYear();
  const monthDiff = end.getMonth() - start.getMonth();
  const dayDiff = end.getDate() - start.getDate();
  
  let totalMonths = yearDiff * 12 + monthDiff;
  
  // Add fractional part based on days
  if (dayDiff > 0) {
    const daysInEndMonth = new Date(end.getFullYear(), end.getMonth() + 1, 0).getDate();
    totalMonths += dayDiff / daysInEndMonth;
  }
  
  return Math.max(totalMonths, 0);
};
